import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Target, Trophy } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { band } from "./sleepFields";

const DURATIONS = [];
for (let m = 300; m <= 600; m += 15) DURATIONS.push(m);

const label = (m) => {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
};

/** Highest-scoring night of the period — tap to open it. */
export const BestNightBadge = ({ best, onOpen, dayHeading }) => {
  if (!best) return null;
  const tone = band(best.score_label);
  return (
    <button
      type="button"
      onClick={() => onOpen(best.date)}
      className="w-full text-left rounded-2xl border px-4 py-3 flex items-center gap-3 transition-colors hover:brightness-[0.98]"
      style={{ background: tone.bg, borderColor: `${tone.ring}33` }}
      data-testid="sleep-best-night"
    >
      <span className="p-2 rounded-full flex-shrink-0" style={{ background: "#ffffffaa", color: tone.fg }}>
        <Trophy className="w-4 h-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[10px] tracking-overline uppercase" style={{ color: tone.fg }}>
          Best night of this period
        </span>
        <span className="block text-sm font-medium truncate">
          {dayHeading(best.date)} · {best.asleep_text || "—"}
          {best.efficiency_percent !== null && best.efficiency_percent !== undefined
            && ` · ${best.efficiency_percent}% efficiency`}
        </span>
      </span>
      <span className="text-right flex-shrink-0">
        <span className="block font-editorial text-3xl leading-none" style={{ color: tone.ring }}>
          {best.score}
        </span>
        <span className="block text-[10px]" style={{ color: tone.fg }}>{best.score_label}</span>
      </span>
    </button>
  );
};

export const GoalSummary = ({ goal, nightsMet, nights, onEdit }) => (
  <Card className="rounded-2xl" data-testid="sleep-goal-summary">
    <CardContent className="p-4 flex items-center gap-3">
      <span className="p-2 rounded-full bg-primary/10 text-primary flex-shrink-0">
        <Target className="w-4 h-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] tracking-overline uppercase text-muted-foreground">Your sleep goal</p>
        <p className="text-sm font-medium" data-testid="sleep-goal-text">
          {goal
            ? `${goal.duration_text} · ${goal.window_text}`
            : "No goal set yet"}
        </p>
        {goal && nightsMet !== null && nightsMet !== undefined && (
          <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="sleep-goal-met">
            You hit it on {nightsMet} of {nights} night{nights === 1 ? "" : "s"} this period
          </p>
        )}
      </div>
      <Button
        variant="outline" size="sm" className="rounded-full text-xs flex-shrink-0"
        onClick={onEdit}
        data-testid="sleep-goal-edit"
      >
        {goal ? "Edit goal" : "Set sleep goal"}
      </Button>
    </CardContent>
  </Card>
);

export const GoalDialog = ({ open, onOpenChange, goal, onSave, onClear, saving }) => {
  const [duration, setDuration] = useState(480);
  const [bedtime, setBedtime] = useState("22:30");
  const [wake, setWake] = useState("06:30");

  useEffect(() => {
    if (!open) return;
    setDuration(goal?.duration_minutes ?? 480);
    setBedtime(goal?.bedtime ?? "22:30");
    setWake(goal?.wake_time ?? "06:30");
  }, [open, goal]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="sleep-goal-dialog">
        <DialogHeader>
          <DialogTitle className="font-editorial text-2xl">Your sleep goal</DialogTitle>
          <DialogDescription>
            Your target only draws a line to aim for — it never changes how your sleep score is worked out.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div>
            <Label className="text-xs">Sleep duration</Label>
            <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
              <SelectTrigger className="mt-1.5 rounded-xl" data-testid="sleep-goal-duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATIONS.map((m) => (
                  <SelectItem key={m} value={String(m)}>{label(m)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs" htmlFor="goal-bedtime">In bed by</Label>
              <Input
                id="goal-bedtime" type="time" value={bedtime}
                onChange={(e) => setBedtime(e.target.value)}
                className="mt-1.5 rounded-xl" data-testid="sleep-goal-bedtime"
              />
            </div>
            <div>
              <Label className="text-xs" htmlFor="goal-wake">Up by</Label>
              <Input
                id="goal-wake" type="time" value={wake}
                onChange={(e) => setWake(e.target.value)}
                className="mt-1.5 rounded-xl" data-testid="sleep-goal-wake"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {goal && (
            <Button
              variant="ghost" className="rounded-full text-xs"
              onClick={onClear} disabled={saving}
              data-testid="sleep-goal-clear"
            >
              Remove goal
            </Button>
          )}
          <Button
            className="rounded-full"
            disabled={saving || !bedtime || !wake}
            onClick={() => onSave({ duration_minutes: duration, bedtime, wake_time: wake })}
            data-testid="sleep-goal-save"
          >
            {saving ? "Saving…" : "Save goal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
